/**
 * UTM / campaign attribution capture.
 *
 * - First touch is persisted in localStorage (survives sessions, used for
 *   "what originally brought this buyer in").
 * - Last touch is persisted in sessionStorage (the campaign for THIS visit).
 * - Every analytics payload can be enriched with `attributionProps()`.
 *
 * All reads/writes are guarded — private mode / SSR are silent no-ops.
 */

const FIRST_TOUCH_KEY = "attr_first_touch";
const LAST_TOUCH_KEY = "attr_last_touch";

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
] as const;

const CLICK_ID_KEYS = ["gclid", "fbclid", "msclkid", "ttclid", "li_fat_id"] as const;

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  utm_id?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  ttclid?: string;
  li_fat_id?: string;
  referrer?: string;
  landing_path?: string;
  ts?: number;
}

const safeRead = (storage: Storage | undefined, key: string): Attribution | null => {
  try {
    const raw = storage?.getItem(key);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
};

const safeWrite = (storage: Storage | undefined, key: string, value: Attribution) => {
  try {
    storage?.setItem(key, JSON.stringify(value));
  } catch {
    /* storage blocked — attribution is best-effort */
  }
};

/** Parse the current URL (plus referrer) into an attribution record. */
export function parseAttributionFromUrl(search = window.location.search): Attribution {
  const params = new URLSearchParams(search);
  const attr: Attribution = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) attr[key] = v.slice(0, 200);
  }
  for (const key of CLICK_ID_KEYS) {
    const v = params.get(key);
    if (v) attr[key] = v.slice(0, 200);
  }
  const ref = document.referrer;
  if (ref && !ref.startsWith(window.location.origin)) attr.referrer = ref.slice(0, 300);
  attr.landing_path = window.location.pathname;
  attr.ts = Date.now();
  return attr;
}

const hasSignal = (attr: Attribution) =>
  UTM_KEYS.some((k) => attr[k]) ||
  CLICK_ID_KEYS.some((k) => attr[k]) ||
  Boolean(attr.referrer);

/**
 * Capture attribution for the current page load. Call once per route change;
 * it only overwrites stored values when the URL carries a real campaign
 * signal, so internal navigation never wipes the original source.
 */
export function captureAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  const current = parseAttributionFromUrl();

  if (hasSignal(current)) {
    safeWrite(window.sessionStorage, LAST_TOUCH_KEY, current);
    if (!safeRead(window.localStorage, FIRST_TOUCH_KEY)) {
      safeWrite(window.localStorage, FIRST_TOUCH_KEY, current);
    }
  } else if (!safeRead(window.sessionStorage, LAST_TOUCH_KEY)) {
    // Direct/organic entry — still record the landing path for this session.
    safeWrite(window.sessionStorage, LAST_TOUCH_KEY, current);
  }

  return getAttribution();
}

/** Last-touch attribution for this session (falls back to first touch). */
export function getAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  return (
    safeRead(window.sessionStorage, LAST_TOUCH_KEY) ??
    safeRead(window.localStorage, FIRST_TOUCH_KEY) ??
    {}
  );
}

/** First-touch attribution (the very first campaign that brought them in). */
export function getFirstTouchAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  return safeRead(window.localStorage, FIRST_TOUCH_KEY) ?? {};
}

/**
 * Flat props for analytics payloads: last-touch as `utm_*`, first-touch
 * mirrored under `ft_*` so you can compare originating vs converting source.
 */
export function attributionProps(): Record<string, string | number> {
  const last = getAttribution();
  const first = getFirstTouchAttribution();
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(last)) {
    if (k === "ts" || v === undefined) continue;
    out[k] = v as string;
  }
  for (const key of UTM_KEYS) {
    const v = first[key];
    if (v) out[`ft_${key}`] = v;
  }
  if (first.referrer) out.ft_referrer = first.referrer;
  return out;
}

/**
 * Attribution payload for lead submissions (MASTERCLASS_URL /
 * STRATEGY_SESSION_URL). Captures the current URL first so a fresh
 * `?utm_*` / click-id always wins over anything stored earlier, then
 * layers stored last-touch + first-touch values underneath.
 */
export function leadAttributionPayload(): Record<string, string | number> {
  if (typeof window === "undefined") return {};

  // Persist whatever campaign signal is in the URL right now.
  captureAttribution();

  const live = parseAttributionFromUrl();
  const out: Record<string, string | number> = { ...attributionProps() };

  for (const key of [...UTM_KEYS, ...CLICK_ID_KEYS] as readonly string[]) {
    const v = (live as Record<string, unknown>)[key];
    if (typeof v === "string" && v) out[key] = v;
  }
  if (live.referrer) out.referrer = live.referrer;

  out.page_url = window.location.href.slice(0, 500);
  out.page_path = window.location.pathname;
  if (window.location.search) out.query_string = window.location.search.slice(0, 500);

  return out;
}
